import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Flag, Ban, Send } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

export function CafeChat({ room }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = useCallback(() => api.get(`/cafe/rooms/${room}/messages`).then(({ data }) => setMsgs(data)), [room]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/cafe/rooms/${room}/messages`, { text });
      setText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const act = async (url, ok, body = {}) => {
    try {
      await api.post(url, body);
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex h-full flex-col" data-testid="cafe-chat">
      <div className="flex-1 space-y-2 overflow-y-auto pr-1">
        {!msgs.length && <p className="text-xs text-slate-500" data-testid="cafe-chat-empty">Nessun messaggio: inizia tu la conversazione.</p>}
        {msgs.map((m) => (
          <div key={m.id} className="group rounded-xl bg-white/80 px-3 py-2 text-sm" data-testid={`cafe-msg-${m.id}`}>
            <p className="text-[11px] text-slate-500"><b className="text-slate-800">{m.author}</b> · {fmtDateTime(m.at)}</p>
            <p className="text-slate-800">{m.text}</p>
            {user && m.author_id !== user.user_id && (
              <div className="mt-1 flex gap-3 text-[11px] text-slate-400 opacity-0 transition-opacity group-hover:opacity-100">
                <button data-testid={`cafe-report-${m.id}`} onClick={() => act(`/cafe/messages/${m.id}/report`, "Messaggio segnalato ai moderatori", { reason: "segnalato dalla chat" })} className="flex items-center gap-1 hover:text-rose-600"><Flag className="h-3 w-3" />Segnala</button>
                <button data-testid={`cafe-block-${m.id}`} onClick={() => act(`/cafe/block/${m.author_id}`, "Utente bloccato")} className="flex items-center gap-1 hover:text-rose-600"><Ban className="h-3 w-3" />Blocca</button>
              </div>
            )}
          </div>
        ))}
        <div ref={end} />
      </div>
      {user ? (
        <form onSubmit={send} className="mt-2 flex gap-2">
          <Input data-testid="cafe-chat-input" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio (niente dati sanitari o personali)" />
          <button data-testid="cafe-chat-send" disabled={!text.trim()} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-600 text-white disabled:opacity-40"><Send className="h-4 w-4" /></button>
        </form>
      ) : <p className="mt-2 rounded-xl bg-amber-50 p-2 text-xs text-amber-800" data-testid="cafe-guest-notice">Modalità ospite: puoi leggere. Accedi per scrivere e comparire tra i presenti.</p>}
    </div>
  );
}
